// Presentation model for the Deal Activity feed: UK day buckets, routine-system detection,
// action grouping (only via a recorded meta.action_id) and row wording.
export const APP_TZ = "Europe/London";

const _dayFmt = new Intl.DateTimeFormat("en-CA", { timeZone: APP_TZ, year: "numeric", month: "2-digit", day: "2-digit" });
const _timeFmt = new Intl.DateTimeFormat("en-GB", { timeZone: APP_TZ, hour: "2-digit", minute: "2-digit", hour12: false });

export const ukDayKey = (ms) => _dayFmt.format(new Date(ms));
export const ukTime = (ms) => _timeFmt.format(new Date(ms));

export function ukDayLabel(dayKey, nowMs = Date.now()) {
  const today = ukDayKey(nowMs);
  if (dayKey === today) return "Today";
  const [ty, tm, td] = today.split("-").map(Number);
  if (dayKey === new Date(Date.UTC(ty, tm - 1, td - 1)).toISOString().slice(0, 10)) return "Yesterday";
  const [y, m, d] = dayKey.split("-").map(Number);
  return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString("en-GB", {
    weekday: "long", day: "numeric", month: "long", timeZone: "UTC", ...(y !== ty ? { year: "numeric" } : {}),
  });
}

// Newest first; equal timestamps fall back to id so the order never shuffles between renders.
export const compareEvents = (a, b) => (b.atMs - a.atMs) || String(b.id).localeCompare(String(a.id));

const MATERIAL_TYPES = new Set([
  "stage_change", "status_change", "exchange_updated", "completion_updated",
  "risk_flagged", "milestone_completed", "milestone_reopened",
]);
const ROUTINE_TYPES = new Set(["reminder_sent", "automation_fired", "document_downloaded"]);

export function isRoutineSystem(e) {
  const t = e?.meta?.activityType;
  const raw = e?.meta?.raw || {};
  if (!t || MATERIAL_TYPES.has(t) || raw.severity === "high" || e?.meta?.priority === "critical") return false;
  if (ROUTINE_TYPES.has(t)) return true;
  if (t === "risk_cleared") return raw.auto === true;
  if (t === "system") return !e.createdById || !!raw.demo_source || raw.auto === true;
  return false;
}

const ROLE_TEXT = {
  solicitor_seller: "Seller's solicitor", seller_solicitor: "Seller's solicitor", seller_conveyancer: "Seller's conveyancer",
  solicitor_buyer: "Buyer's solicitor", buyer_solicitor: "Buyer's solicitor", buyer_conveyancer: "Buyer's conveyancer",
  broker: "Mortgage broker", mortgage_broker: "Mortgage broker", agent: "Agent", estate_agent: "Estate agent",
  buyer: "Buyer", seller: "Seller", lender: "Lender", surveyor: "Surveyor",
  managing_agent: "Managing agent", freeholder: "Freeholder",
};
export function roleText(role) {
  if (!role) return null;
  const key = String(role).trim().toLowerCase().replace(/[\s-]+/g, "_");
  return ROLE_TEXT[key] || String(role).replace(/_/g, " ").replace(/^\w/, (c) => c.toUpperCase());
}
const ROLE_KEY_RE = /\b(solicitor_seller|seller_solicitor|solicitor_buyer|buyer_solicitor|seller_conveyancer|buyer_conveyancer|mortgage_broker|managing_agent|estate_agent)\b/g;
// Role keys become party labels; any other snake_case key (stage / field ids) reads as words.
export const humanizeRoleKeys = (text) => (typeof text === "string"
  ? text.replace(ROLE_KEY_RE, (k) => ROLE_TEXT[k]).replace(/\b[a-z]+(?:_[a-z]+)+\b/g, (k) => k.replace(/_/g, " "))
  : text);

// Recorded person for manual actions, ChainFlow for automated writes, nothing when unknown.
export function actorFor(e) {
  if (!e) return null;
  if (e.source === "manual") return "You";
  if (e.source === "canonical") {
    if (e.createdById) return e.actorName || null;
    return e.meta?.raw?.demo_source ? null : "ChainFlow";
  }
  return e.actorName || e.participantName || null;
}

export const isMilestoneEvent = (e) => e?.kind === "milestone" && !e?.meta?.narrative
  && (e?.source === "milestones" || ["milestone_completed", "milestone_reopened"].includes(e?.meta?.activityType));

const _cap = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);
const _reEsc = (s) => String(s).replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

// Milestone-led title: the milestone itself, never the person who recorded it.
export function milestoneTitle(a, labelsById = {}) {
  const m = a?.meta || {};
  const id = m.milestone_id || m.stage_id || m.milestone_stage_id;
  const label = id ? labelsById[String(id)] : null;
  const reopened = a?.activity_type === "milestone_reopened";
  if (label) return reopened ? `${label} reopened` : label;
  let t = String(a?.title || "").trim();
  const name = a?.actor_name;
  if (name && t.startsWith(name)) t = t.slice(name.length).replace(/^\s*\([^)]*\)/, "");
  else t = t.replace(/^(Agent|ChainFlow)\b/, "").replace(new RegExp(`^(${Object.keys(ROLE_TEXT).map(_reEsc).join("|")})\\b`, "i"), "");
  t = t.trim().replace(/^(confirmed|marked)\s+/i, "");
  if (reopened && /^reopened\s+/i.test(t)) return `${_cap(t.replace(/^reopened\s+/i, ""))} reopened`;
  return _cap(t) || a?.title || "";
}

// "Recorded by Rhys Drewell · 21:21" — the recorder, not the doer.
export const recordedByLine = (e) => {
  const actor = actorFor(e);
  return [actor ? `Recorded by ${actor}` : "Recorded", ukTime(e.atMs)].join(" · ");
};

const TYPE_LABEL = {
  email: "Email", message: "WhatsApp", phone: "Call", sms: "SMS", chase: "Chase",
  note: "Note", internal_note: "Internal note", document_upload: "Document",
  document_request: "Document request", milestone: "Milestone",
};

const _lowerFirst = (s) => (s ? s.charAt(0).toLowerCase() + s.slice(1) : s);

// Second line: actor · what kind of entry / who it was with. Never repeats the same text twice.
export function contextLine(e, actor, title = "") {
  const raw = e?.meta?.raw || {};
  const name = e.contactName || raw.participant_name || e.raw?.participantName || null;
  const role = roleText(raw.role_label || raw.participant_role || e.raw?.roleLabel || null);
  const recipient = name && role && name !== role ? `${name} (${_lowerFirst(role)})` : (name || (role && _lowerFirst(role)));
  const type = TYPE_LABEL[e.kind] || null;
  const inbound = ["in", "inbound", "incoming", "received"].includes(String(raw.direction || "").toLowerCase());
  if (inbound && actor && e.createdById) actor = `Logged by ${actor}`;
  const ctx = type && recipient && type !== "Note" ? `${type} ${e.kind === "phone" ? "with" : inbound ? "from" : "to"} ${recipient}` : type;
  const parts = [];
  const shownTitle = String(title || "").toLowerCase();
  [actor, ctx].forEach((p) => {
    const low = String(p || "").toLowerCase();
    const inTitle = low && new RegExp(`(^|\\W)${low.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}($|\\W)`).test(shownTitle);
    if (p && !inTitle && !parts.some((x) => x.toLowerCase() === low)) parts.push(p);
  });
  return parts.join(" · ");
}

const GROUP_LABELS = {
  milestone_completed: ["milestone completed", "milestones completed"],
  milestone_reopened: ["milestone reopened", "milestones reopened"],
  risk_cleared: ["blocker cleared", "blockers cleared"],
  milestone_target_set: ["date captured", "dates captured"],
  milestone_note_set: ["milestone note", "milestone notes"],
  stage_change: ["stage change", "stage changes"],
  status_change: ["status update", "status updates"],
  reminder_sent: ["reminder sent", "reminders sent"],
  chase: ["chase", "chases"],
  document_upload: ["document added", "documents added"],
  document_request: ["document requested", "documents requested"],
};

export function summariseGroup(events) {
  const counts = new Map();
  events.forEach((e) => {
    const t = e.meta?.activityType || e.kind;
    counts.set(t, (counts.get(t) || 0) + 1);
  });
  const parts = [];
  let other = 0;
  Object.keys(GROUP_LABELS).forEach((t) => {
    const n = counts.get(t);
    if (n) parts.push(`${n} ${GROUP_LABELS[t][n === 1 ? 0 : 1]}`);
  });
  counts.forEach((n, t) => { if (!GROUP_LABELS[t]) other += n; });
  if (other) parts.push(`${other} other ${other === 1 ? "update" : "updates"}`);
  const milestones = events.filter((e) => e.meta?.activityType === "milestone_completed");
  const cols = new Set(events.map((e) => e.meta?.raw?.col).filter(Boolean));
  let title = events[0].title;
  if (milestones.length === 1) title = milestones[0].title;
  else if (milestones.length > 1 && cols.size === 1) {
    const col = [...cols][0];
    title = `${col.charAt(0).toUpperCase()}${col.slice(1)} progression updated`;
  } else if (milestones.length > 1) title = "Progression updated";
  const actors = [...new Set(events.map(actorFor).filter(Boolean))];
  const allMilestones = events.every(isMilestoneEvent);
  return { title, count: events.length, parts, actors, allMilestones, head: milestones[0] || events[0] };
}

// Builds day sections of rows. Events sharing a recorded action_id become one group row;
// a group is shown when any member passes the filter, and always carries every member.
export function buildTimeline(events, { matches, showSystem }) {
  const byAction = new Map();
  events.forEach((e) => {
    const aid = e.meta?.raw?.action_id;
    if (!aid) return;
    if (!byAction.has(aid)) byAction.set(aid, []);
    byAction.get(aid).push(e);
  });
  const visible = (e) => matches(e) && (showSystem || !isRoutineSystem(e));
  const rows = [];
  const seen = new Set();
  events.forEach((e) => {
    const aid = e.meta?.raw?.action_id;
    const members = aid ? byAction.get(aid) : null;
    if (members && members.length > 1) {
      if (seen.has(aid)) return;
      seen.add(aid);
      if (members.some(visible)) rows.push({ __group: true, id: `grp_${aid}`, atMs: members[0].atMs, events: members });
      return;
    }
    if (visible(e)) rows.push(e);
  });
  const days = [];
  rows.forEach((r) => {
    const key = ukDayKey(r.atMs);
    const last = days[days.length - 1];
    if (last && last.key === key) last.rows.push(r);
    else days.push({ key, rows: [r] });
  });
  return days;
}
